import { useEffect, useState } from 'react';
import {
  faCheck,
  faForward,
  faPencil,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
} from '../../../core';
import { surfaceHeaderProps } from '../SurfaceHeader';
import { SURFACE_COLORS } from '../surface-colors';

/** One value the agent proposes for a field. Always a value the platform
 * accepts — the card never lets the user invent one, except through an
 * explicit escape hatch. */
export interface ClarifyOption {
  readonly value: string;
  readonly label: string;
  /** Supporting detail, e.g. a NAICS code's industry description. */
  readonly note?: string;
  readonly recommended?: boolean;
}

/** What the escape hatch offers when none of the proposals fit. `options` keeps
 * the answer inside a known set; `text` is the only free entry on the card, and
 * it exists only once the user has rejected every proposal. */
export interface ClarifyEscape {
  readonly label: string;
  readonly kind: 'text' | 'options';
  readonly placeholder?: string;
  readonly options?: readonly ClarifyOption[];
}

export interface Clarification {
  /** Answer key — what the pick is written back as, at `/answers/<field>`. */
  readonly field: string;
  /** The question, phrased as the user should read it. */
  readonly message: string;
  readonly options?: readonly ClarifyOption[];
  /** Short chip label for the question (~12 chars). Optional: a question that
   * reads on its own doesn't need one. */
  readonly fieldLabel?: string;
  /** When true the user may pick several options and the answer is written as
   * an array rather than a string. */
  readonly multiSelect?: boolean;
  /** The user's own words, shown struck through so they can see what wasn't
   * taken. Only meaningful when the card is correcting an answer they gave. */
  readonly operatorAnswer?: string;
  readonly reason?: 'invalid' | 'ambiguous';
  readonly escape?: ClarifyEscape;
}

/** Written into `/results/<id>` by the host once Submit settles — never sent by
 * the agent up front. `updated` means the write landed, `invalid` means it was
 * rejected atomically with per-field reasons. */
export interface ClarifySubmitResult {
  readonly status?: string;
  readonly remaining?: readonly (string | { readonly field?: string })[];
  readonly errors?: readonly {
    readonly field?: string;
    readonly reason?: string;
  }[];
  readonly updatedFields?: readonly string[];
}

/** Written into the same path when the dispatch itself failed, so one binding
 * carries both outcomes. Distinguished by the `error` flag. */
export interface ClarifySubmitError {
  readonly error: true;
  readonly message?: string;
}

export interface AskQuestionsProps {
  readonly clarifications?: readonly Clarification[];
  readonly title?: string;
  readonly footnote?: string;
  readonly result?: ClarifySubmitResult | ClarifySubmitError;
  readonly pending?: boolean;
  /** Freezes every control without settling the card. Set once this surface is
   * no longer the latest turn (the user has moved on) so a stale card can't be
   * re-answered — the same rule PromptQuickReply follows. */
  readonly locked?: boolean;
  /** Called for each pick so the host can mirror it into the data model — the
   * Submit action reads from there, not from this component's state. A
   * multi-select question reports an array. */
  readonly onAnswer?: (field: string, value: string | string[]) => void;
  readonly onSubmit?: () => void;
  /** Whether the user submitted the card. A card that settles without it was
   * skipped — the user answered in chat instead. */
  readonly submitted?: boolean;
}

/** Sentinel for the "Neither…" escape row, so a selection can't collide with a
 * real option value. */
const ESCAPE = '__escape__';

function isSubmitError(
  value: ClarifySubmitResult | ClarifySubmitError | undefined
): value is ClarifySubmitError {
  return Boolean(value) && (value as ClarifySubmitError).error === true;
}

/** Every branch below reads exactly one of these phases, so `submitting` /
 * `done` / `failed` can never disagree with each other (same reasoning as
 * EditQuote's SaveState). */
export type ClarifyPhase =
  | { readonly phase: 'asking' }
  | { readonly phase: 'submitting' }
  | { readonly phase: 'done'; readonly result: ClarifySubmitResult }
  | {
      readonly phase: 'failed';
      readonly message?: string;
      readonly errors: readonly {
        readonly field?: string;
        readonly reason?: string;
      }[];
    };

/**
 * Pure derivation, exported so the phase transitions are unit-tested directly
 * rather than inferred from the DOM.
 *
 * A rejected write is a first-class outcome, not an error state to bail on: the
 * host is the only validator here, so its reasons are what tells the user which
 * answer to change. That's why `failed` carries `errors` and the card stays
 * interactive.
 */
export function deriveClarifyPhase(
  result: ClarifySubmitResult | ClarifySubmitError | undefined,
  pending: boolean
): ClarifyPhase {
  if (pending) return { phase: 'submitting' };
  if (!result) return { phase: 'asking' };
  if (isSubmitError(result)) {
    return { phase: 'failed', message: result.message, errors: [] };
  }
  if (result.status === 'updated') return { phase: 'done', result };
  return {
    phase: 'failed',
    errors: result.errors ?? [],
    message: result.status ? undefined : 'Those answers were rejected.',
  };
}

/** Name a `remaining` entry, which arrives as either a bare key or a
 * `{field, description}` row depending on which host produced it. */
function remainingLabel(entry: string | { readonly field?: string }): string {
  return typeof entry === 'string' ? entry : entry.field ?? '';
}

/** Built from statements rather than a nested ternary (house rule). */
function savedSummary(result: ClarifySubmitResult): string {
  const names = (result.remaining ?? []).map(remainingLabel).filter(Boolean);
  if (names.length === 0) return 'Answers saved.';
  return `Answers saved. Still needed: ${names.join(', ')}.`;
}

/** Title, message and tone resolved together so they can't disagree. A write in
 * flight or saved counts as submitted; anything else that settled was skipped. */
type SettledTone = 'submitted' | 'skipped';

interface SettledView {
  readonly title: string;
  readonly message: string;
  readonly tone: SettledTone;
}

function settledView(state: ClarifyPhase, submitted: boolean): SettledView {
  const wasSubmitted =
    state.phase === 'done' || state.phase === 'submitting' || submitted;
  if (!wasSubmitted) {
    return { title: 'Questions skipped', message: 'Skipped', tone: 'skipped' };
  }
  const message =
    state.phase === 'done'
      ? savedSummary(state.result)
      : 'Your answers have been submitted.';
  return { title: 'Answers submitted', message, tone: 'submitted' };
}

/** Colours and icon per tone. */
const SETTLED_STYLE: Record<
  SettledTone,
  { readonly bg: string; readonly fg: string; readonly icon: typeof faCheck }
> = {
  submitted: {
    bg: SURFACE_COLORS.settledSubmitted,
    fg: 'green-6',
    icon: faCheck,
  },
  skipped: { bg: SURFACE_COLORS.settledSkipped, fg: 'gray-6', icon: faForward },
};

/** Centered icon-and-line shown once the card is done with. */
function SettledNotice({
  tone,
  message,
}: {
  readonly tone: SettledTone;
  readonly message: string;
}) {
  const style = SETTLED_STYLE[tone];
  return (
    <Group
      justify="center"
      align="center"
      gap="xs"
      wrap="nowrap"
      mt="md"
      py={10}
      px="md"
      style={{
        borderRadius: 8,
        backgroundColor: style.bg,
      }}
    >
      <FontAwesomeIcon
        icon={style.icon}
        style={{
          width: 16,
          height: 16,
          color: `var(--mantine-color-${style.fg})`,
        }}
      />
      <Text size="sm" ta="center">
        {message}
      </Text>
    </Group>
  );
}

/** One selectable proposal. Radio semantics for a single-answer question and
 * checkbox semantics for a multi-select one — these are answers to one
 * question, and a screen reader should hear them that way. */
function ProposalRow({
  index,
  label,
  note,
  recommended,
  selected,
  multi,
  disabled,
  onSelect,
  children,
}: {
  readonly index: number;
  readonly label: string;
  readonly note?: string;
  readonly recommended?: boolean;
  readonly selected: boolean;
  readonly multi?: boolean;
  readonly disabled?: boolean;
  readonly onSelect: () => void;
  readonly children?: React.ReactNode;
}) {
  const select = () => {
    if (!disabled) onSelect();
  };
  return (
    <Box
      onClick={select}
      role={multi ? 'checkbox' : 'radio'}
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(event) => {
        // Only the row itself toggles on Enter/Space — a nested control (the
        // escape text field) keeps its own keys, so the user can type spaces.
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          select();
        }
      }}
      px="sm"
      py={8}
      style={{
        borderRadius: 8,
        cursor: disabled ? 'default' : 'pointer',
        backgroundColor: selected
          ? SURFACE_COLORS.selected
          : SURFACE_COLORS.inset,
        border: `1px solid ${
          selected ? 'var(--mantine-color-blue-6)' : 'transparent'
        }`,
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
        <Box style={{ minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={400}>
              {label}
            </Text>
            {recommended && (
              <Badge size="xs" radius="sm" variant="light" color="blue">
                Recommended
              </Badge>
            )}
          </Group>
          {note && (
            <Text size="xs" c="dimmed" lh={1.4} mt={2}>
              {note}
            </Text>
          )}
          {children}
        </Box>
        <Badge
          size="sm"
          radius="sm"
          variant="default"
          style={{ flexShrink: 0 }}
          aria-hidden
        >
          {index}
        </Badge>
      </Group>
    </Box>
  );
}

/**
 * A closed-set question card: asks the user a small number of questions, one at
 * a time, each offering the values the host actually accepts.
 *
 * Generic by design — it knows nothing about what it is asking for. Callers
 * supply the questions, the labels, and (optionally) the answer the user
 * already gave that is being corrected.
 *
 * Two stages: one question per step, then the settled outcome.
 *
 * One rule shapes the card, and it is structural rather than a convention to
 * remember: no free-form input is offered without proposed content first. The
 * one text field lives behind an escape hatch the user has to pick after
 * rejecting every proposal.
 */
export function AskQuestions({
  clarifications,
  title,
  footnote,
  result,
  pending,
  locked: lockedProp,
  onAnswer,
  onSubmit,
  submitted,
}: AskQuestionsProps) {
  // Drop malformed entries rather than rendering an empty radiogroup: an agent
  // that emits a question with nothing to propose has given the user no way to
  // answer it, and the card would dead-end.
  const questions = (clarifications ?? []).filter(
    (item) => item?.field && (item.options?.length ?? 0) > 0
  );
  const state = deriveClarifyPhase(result, pending === true);

  const [step, setStep] = useState(0);
  /** field → the chosen option value(s), or ESCAPE while the hatch is open. */
  const [choices, setChoices] = useState<Record<string, string | string[]>>({});
  /** field → the value entered/picked inside the escape hatch. */
  const [escapes, setEscapes] = useState<Record<string, string>>({});

  const current = questions[step];
  const isLast = step >= questions.length - 1;
  const locked =
    lockedProp === true ||
    state.phase === 'submitting' ||
    state.phase === 'done';

  // A rejected submit means nothing was written, so put the user back on the
  // first question with the host's reasons attached — leaving them on the last
  // step would hide the answer that was actually refused.
  useEffect(() => {
    if (state.phase === 'failed') setStep(0);
  }, [state.phase]);

  /** True when `option` is among the picks for a multi-select `field`. */
  const isPicked = (field: string, value: string): boolean => {
    const choice = choices[field];
    if (Array.isArray(choice)) return choice.includes(value);
    return choice === value;
  };

  const answerFor = (field: string): string | string[] | undefined => {
    const choice = choices[field];
    if (!choice) return undefined;
    if (Array.isArray(choice)) return choice.length > 0 ? choice : undefined;
    if (choice !== ESCAPE) return choice;
    const escaped = escapes[field]?.trim();
    return escaped || undefined;
  };

  const pendingValue = current ? answerFor(current.field) : undefined;

  /** Mirror a pick into the host data model as it happens. Doing it per pick
   * rather than once on Submit means a value is never held only in local state
   * — the Submit action reads the data model, so anything not pushed here
   * simply wouldn't be written. */
  const commit = (field: string, value: string | string[] | undefined) => {
    if (value && value.length > 0) onAnswer?.(field, value);
  };

  /** A multi-select pick toggles; a single-answer pick replaces. */
  const selectOption = (item: Clarification, value: string) => {
    const field = item.field;
    if (!item.multiSelect) {
      setChoices((prev) => ({ ...prev, [field]: value }));
      commit(field, value);
      return;
    }
    const existing = choices[field];
    const current = Array.isArray(existing) ? existing : [];
    const next = current.includes(value)
      ? current.filter((entry) => entry !== value)
      : [...current, value];
    setChoices((prev) => ({ ...prev, [field]: next }));
    commit(field, next);
  };

  const selectEscape = (field: string) => {
    setChoices((prev) => ({ ...prev, [field]: ESCAPE }));
    // Nothing to commit yet — the hatch has no value until the user supplies one.
  };

  const setEscapeValue = (field: string, value: string) => {
    setEscapes((prev) => ({ ...prev, [field]: value }));
    commit(field, value.trim());
  };

  const advance = () => {
    if (!pendingValue) return;
    if (isLast) {
      onSubmit?.();
      return;
    }
    setStep(step + 1);
  };

  const goBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const serverReasonFor = (field: string): string | undefined => {
    if (state.phase !== 'failed') return undefined;
    return state.errors.find((entry) => entry.field === field)?.reason;
  };

  /** Reasons naming no visible field — a transport failure reports one with an
   * empty field name — would otherwise render against nothing and leave the
   * card silent about why it failed. */
  const cardLevelMessage = (): string | undefined => {
    if (state.phase !== 'failed') return undefined;
    if (state.message) return state.message;
    const fields = new Set(questions.map((q) => q.field));
    const orphaned = state.errors
      .filter((entry) => !entry.field || !fields.has(entry.field))
      .map((entry) => entry.reason)
      .filter(Boolean);
    return orphaned.length > 0 ? orphaned.join('; ') : undefined;
  };

  const headerBadge = (): string => {
    if (questions.length === 1) return '1 question';
    return `${questions.length} questions`;
  };

  // Settle once locked or a write lands; a rejected write stays on the questions
  // so the user can fix it. Answers aren't echoed — the follow-up chat turn is
  // the record, and client state doesn't survive a surface rebuild anyway.
  const showSettled =
    state.phase === 'done' || (locked && state.phase !== 'failed');
  const showQuestion = !showSettled && Boolean(current);
  const settled = showSettled ? settledView(state, submitted === true) : null;

  return (
    <Paper
      radius="md"
      p="md"
      withBorder
      style={{
        width: '100%',
        borderColor: SURFACE_COLORS.cardBorder,
        backgroundColor: 'var(--mantine-color-body)',
      }}
    >
      {/* Header — a plain title, not a small-caps eyebrow: this card is a
          question addressed to the user, not a labelled readout. Whatever the
          card is about was already named in the chat turn that introduced it,
          so it is not repeated here. */}
      <Group
        justify="space-between"
        align="flex-start"
        wrap="nowrap"
        gap="sm"
        {...surfaceHeaderProps('md', 'none')}
      >
        <Text fw={700} size="md" lh={1.3} style={{ minWidth: 0 }}>
          {settled ? settled.title : title || 'A few questions'}
        </Text>
        {/* Question-count pill while asking; nothing to count once settled. */}
        {!showSettled && (
          <Badge
            color="yellow"
            variant="light"
            size="sm"
            radius="sm"
            style={{ flexShrink: 0 }}
          >
            {headerBadge()}
          </Badge>
        )}
      </Group>

      {/* Stage 1 — one question at a time. */}
      {showQuestion && current && (
        <>
          {cardLevelMessage() && (
            <Alert
              mt="md"
              radius="md"
              color="red"
              variant="light"
              icon={
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  style={{ width: 16, height: 16 }}
                />
              }
            >
              <Text size="sm">{cardLevelMessage()}</Text>
            </Alert>
          )}

          <Group gap={8} wrap="nowrap" align="flex-start" mt="md">
            <Badge
              color="yellow"
              variant="light"
              size="md"
              radius="sm"
              style={{ flexShrink: 0 }}
            >
              {step + 1}/{questions.length}
            </Badge>
            <Box style={{ minWidth: 0 }}>
              <Text size="md" fw={700} lh={1.45}>
                {current.message}
              </Text>
              {/* Only rendered for a card that is correcting something the user
                  said — a plain question has nothing to quote back. */}
              {current.operatorAnswer && (
                <Text size="xs" c="dimmed" mt={4}>
                  {current.fieldLabel ? `${current.fieldLabel} · ` : ''}you said
                  “{current.operatorAnswer}”
                </Text>
              )}
              {current.multiSelect && (
                <Text size="xs" c="dimmed" mt={4}>
                  Pick as many as apply.
                </Text>
              )}
              {serverReasonFor(current.field) && (
                <Text size="xs" c="red.6" fs="italic" mt={4}>
                  {serverReasonFor(current.field)}
                </Text>
              )}
            </Box>
          </Group>

          <Stack
            gap={4}
            mt="sm"
            role={current.multiSelect ? 'group' : 'radiogroup'}
            aria-label={current.fieldLabel || current.message}
          >
            {(current.options ?? []).map((option, index) => (
              <ProposalRow
                key={option.value}
                index={index + 1}
                label={option.label}
                note={option.note}
                recommended={option.recommended}
                selected={isPicked(current.field, option.value)}
                multi={current.multiSelect}
                disabled={locked}
                onSelect={() => selectOption(current, option.value)}
              />
            ))}

            {current.escape && (
              <ProposalRow
                index={(current.options?.length ?? 0) + 1}
                label={current.escape.label}
                selected={choices[current.field] === ESCAPE}
                disabled={locked}
                onSelect={() => selectEscape(current.field)}
              >
                {choices[current.field] === ESCAPE &&
                  current.escape.kind === 'text' && (
                    <TextInput
                      mt={8}
                      size="sm"
                      disabled={locked}
                      placeholder={current.escape.placeholder}
                      value={escapes[current.field] ?? ''}
                      aria-label={
                        current.escape.placeholder ||
                        current.fieldLabel ||
                        current.message
                      }
                      leftSection={
                        <FontAwesomeIcon
                          icon={faPencil}
                          style={{ width: 13, height: 13 }}
                        />
                      }
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) =>
                        setEscapeValue(current.field, event.currentTarget.value)
                      }
                    />
                  )}

                {choices[current.field] === ESCAPE &&
                  current.escape.kind === 'options' && (
                    <Group gap={6} mt={8} wrap="wrap">
                      {(current.escape.options ?? []).map((option) => (
                        <Button
                          key={option.value}
                          size="compact-xs"
                          color="blue"
                          disabled={locked}
                          variant={
                            escapes[current.field] === option.value
                              ? 'filled'
                              : 'default'
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            setEscapeValue(current.field, option.value);
                          }}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </Group>
                  )}
              </ProposalRow>
            )}
          </Stack>

          {/* On the first question there is nowhere to go back to, so Back is
              absent rather than present-and-dead; Next then takes the row on
              its own and stays where it has always been, on the right. */}
          <Group
            justify={step === 0 ? 'flex-end' : 'space-between'}
            mt="md"
            wrap="nowrap"
          >
            {step > 0 && (
              <Button
                size="compact-sm"
                radius="md"
                miw={72}
                variant="default"
                disabled={locked}
                onClick={goBack}
              >
                Back
              </Button>
            )}
            <Button
              size="compact-sm"
              color="blue"
              radius="md"
              miw={72}
              onClick={advance}
              disabled={!pendingValue || locked}
            >
              {isLast ? 'Done' : 'Next'}
            </Button>
          </Group>

          {footnote && (
            <Text size="xs" c="dimmed" mt={8}>
              {footnote}
            </Text>
          )}
        </>
      )}

      {/* Settled: submitted or skipped, no answers echoed. */}
      {settled && (
        <SettledNotice tone={settled.tone} message={settled.message} />
      )}

      {!showSettled && questions.length === 0 && (
        <Text size="sm" c="dimmed" mt="md">
          Nothing to confirm.
        </Text>
      )}
    </Paper>
  );
}
